
import { Component, Vue } from 'vue-property-decorator';
import { CollectionProxy } from 'spraypaint/lib-esm/proxies';
import ObservedPromise from 'spa-ionata-admin/lib/services/ObservedPromise';
import PersistStore from 'spa-ionata-admin/lib/services/PersistStore';
import ErrorAlert from 'spa-ionata-admin/lib/components/ErrorAlert.vue';
import Business from '../models/Business';
import BusinessTile from './BusinessTile.vue';
import BusinessFeatured from './BusinessFeatured.vue';
import AnOverline from './AnOverline.vue';
import { isDev } from '@/config';

const _store = new PersistStore('businesses-listing', {
  storage: sessionStorage,
  debug: isDev,
});

@Component({
  components: { BusinessTile, BusinessFeatured, AnOverline, ErrorAlert },
})
export default class BusinessListingLanding extends Vue {
  limit = 5;

  dataPromise = new ObservedPromise<CollectionProxy<Business>>();

  get items(): Business[] {
    return this.dataPromise.value?.data || [];
  }

  get columns(): number {
    const { breakpoint } = this.$vuetify;
    if (breakpoint.lgAndUp) {
      return 4;
    }
    if (breakpoint.mdAndUp) {
      return 3;
    }
    if (breakpoint.smAndUp) {
      return 2;
    }
    return 3;
  }

  get error() {
    return this.dataPromise.error;
  }

  get featured() {
    const lastWithValidQuote = this.items
      .slice()
      .reverse()
      .find(item => item.hasValidQuote);
    return lastWithValidQuote;
  }

  get dataLoading() {
    return this.dataPromise.isPending;
  }

  get listItems() {
    return this.items.filter(item => item !== this.featured);
  }

  mounted() {
    const persisted = _store.load();
    if (
      typeof persisted === 'object' &&
      'ids' in persisted &&
      'hash' in persisted &&
      'date' in persisted
    ) {
      const { ids, hash } = persisted;
      try {
        if (hash === this.hashCode(ids.join(','))) {
          const promise = Business.where({
            id__in: ids.slice(0, this.limit),
            is_public: true,
          }).all();
          this.dataPromise = new ObservedPromise(promise);
        }
      } catch (err) {
        // ingore
      }
    }
    if (!this.dataPromise.isPending) {
      this.loadData();
    }
  }

  async loadData() {
    const promise = (async () => {
      const items = await Business.random()
        .where({ is_public: true })
        .per(this.limit)
        .all();
      const hasOneWithValidQuote = items.data.some(item => item.hasValidQuote);
      if (!hasOneWithValidQuote && items.data.length === this.limit) {
        const withValidQuote = await Business.random()
          .where({ is_public: true, quote_image__isnull: false })
          .first();
        if (withValidQuote.data?.hasValidQuote) {
          items.data[items.data.length - 1] = withValidQuote.data;
        }
      }
      return items;
    })();
    this.dataPromise = new ObservedPromise(promise);
    try {
      const result = await promise;
      const ids = result.data.map(item => item.id);
      const hash = this.hashCode(ids.join(','));
      const date = +new Date();
      _store.persist({ ids, hash, date });
    } catch (err) {
      // ignore
    }
  }

  hashCode(text: string) {
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      hash = (hash << 5) - hash + text.charCodeAt(i);
      hash = hash & hash;
    }
    return hash;
  }
}
